import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import { couponService, CreateCouponBatchRequest, CouponBatch } from '../services/couponService';

const parseNumberArray = (value: string): number[] => {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v) && v > 0);
};

const CouponCampaigns: React.FC = () => {
  const [name, setName] = useState('');
  const [totalAmount, setTotalAmount] = useState('200');
  const [allowedValuesRaw, setAllowedValuesRaw] = useState('4');
  const [minOrderValue, setMinOrderValue] = useState('25');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createdBatch, setCreatedBatch] = useState<CouponBatch | null>(null);
  const [batches, setBatches] = useState<CouponBatch[]>([]);
  const [editingBatch, setEditingBatch] = useState<CouponBatch | null>(null);
  const [editName, setEditName] = useState('');
  const [editTotalAmount, setEditTotalAmount] = useState('');
  const [editMinOrderValue, setEditMinOrderValue] = useState('');
  const [editStatus, setEditStatus] = useState<CouponBatch['status']>('ACTIVE');

  useEffect(() => {
    const loadBatches = async () => {
      try {
        const result = await couponService.listBatches();
        setBatches(result);
      } catch (err: any) {
        setError(err?.message || 'Erro ao carregar campanhas de cupons');
      }
    };

    loadBatches();
  }, []);

  const openEditModal = (batch: CouponBatch) => {
    setEditingBatch(batch);
    setEditName(batch.name || '');
    setEditTotalAmount(batch.totalAmount.toString());
    setEditMinOrderValue(batch.minOrderValue.toString());
    setEditStatus(batch.status);
  };

  const closeEditModal = () => {
    setEditingBatch(null);
    setEditName('');
    setEditTotalAmount('');
    setEditMinOrderValue('');
  };

  const handleSaveEdit = async () => {
    if (!editingBatch) return;

    const total = Number(editTotalAmount);
    const minOrder = Number(editMinOrderValue);
    if (!total || total <= 0 || !minOrder || minOrder <= 0) {
      setError('Informe valores válidos para total da campanha e mínimo do pedido.');
      return;
    }

    try {
      setLoading(true);
      const updated = await couponService.updateBatch(editingBatch.batchId, {
        name: editName.trim() || editingBatch.name,
        totalAmount: total,
        minOrderValue: minOrder,
        status: editStatus,
      });

      setBatches((old) => old.map((b) => (b.batchId === updated.batchId ? updated : b)));
      if (createdBatch && createdBatch.batchId === updated.batchId) {
        setCreatedBatch(updated);
      }
      closeEditModal();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar campanha de cupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setCreatedBatch(null);

    const allowedValues = parseNumberArray(allowedValuesRaw);
    if (!allowedValues.length || allowedValues.length > 3) {
      setError('Informe entre 1 e 3 valores de cupom, separados por vírgula.');
      return;
    }

    const payload: CreateCouponBatchRequest = {
      name: name.trim(),
      totalAmount: Number(totalAmount),
      allowedValues,
      minOrderValue: Number(minOrderValue),
      startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
    };

    if (!payload.name) {
      setError('Informe o nome da campanha.');
      return;
    }

    if (!payload.totalAmount || payload.totalAmount <= 0) {
      setError('Valor total da campanha deve ser maior que zero.');
      return;
    }

    if (!payload.minOrderValue || payload.minOrderValue <= 0) {
      setError('Valor mínimo do pedido deve ser maior que zero.');
      return;
    }

    setLoading(true);
    try {
      const batch = await couponService.createBatch(payload);
      setCreatedBatch(batch);
      setSuccess('Campanha de cupons criada com sucesso.');

      // Recarregar lista de campanhas após criação
      try {
        const result = await couponService.listBatches();
        setBatches(result);
      } catch (loadErr: any) {
        setError(loadErr?.message || 'Erro ao recarregar campanhas de cupons');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar campanha de cupons');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Campanhas de Cupons
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Crie campanhas de cupons definindo o valor total destinado, os valores possíveis por cupom e o valor mínimo do pedido.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Nome da campanha"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Valor total da campanha (R$)"
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
            />

            <TextField
              label="Valores de cupons (R$), separados por vírgula"
              helperText="Ex.: 4 ou 4,6,10 (máximo 3 valores)"
              value={allowedValuesRaw}
              onChange={(e) => setAllowedValuesRaw(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Valor mínimo do pedido (R$)"
              type="number"
              value={minOrderValue}
              onChange={(e) => setMinOrderValue(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
            />

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Início da campanha"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Fim da campanha"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? 'Criando...' : 'Criar campanha'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      {createdBatch && (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Campanha criada
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ID da campanha (batchId): {createdBatch.batchId}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            <Chip label={`Total: R$ ${createdBatch.totalAmount.toFixed(2)}`} />
            <Chip label={`Saldo: R$ ${createdBatch.remainingAmount.toFixed(2)}`} />
            <Chip label={`Mínimo pedido: R$ ${createdBatch.minOrderValue.toFixed(2)}`} />
            <Chip label={`Status: ${createdBatch.status}`} />
          </Box>
        </Paper>
      )}

      {batches.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Campanhas cadastradas
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campanha</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total (R$)</TableCell>
                <TableCell align="right">Saldo (R$)</TableCell>
                <TableCell align="right">Mín. pedido (R$)</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch) => (
                <TableRow key={batch.batchId} hover>
                  <TableCell>{batch.name || batch.batchId}</TableCell>
                  <TableCell>
                    <Chip label={batch.status} size="small" />
                  </TableCell>
                  <TableCell align="right">{batch.totalAmount.toFixed(2)}</TableCell>
                  <TableCell align="right">{batch.remainingAmount.toFixed(2)}</TableCell>
                  <TableCell align="right">{batch.minOrderValue.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => openEditModal(batch)}
                    >
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!editingBatch} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Editar campanha de cupons
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {editingBatch && (
              <Typography variant="body2" color="text.secondary">
                ID da campanha (batchId): {editingBatch.batchId}
              </Typography>
            )}
            <TextField
              label="Nome da campanha"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Valor total da campanha (R$)"
              type="number"
              value={editTotalAmount}
              onChange={(e) => setEditTotalAmount(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label="Valor mínimo do pedido (R$)"
              type="number"
              value={editMinOrderValue}
              onChange={(e) => setEditMinOrderValue(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label="Status da campanha"
              select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as CouponBatch['status'])}
              fullWidth
            >
              <MenuItem value="ACTIVE">Ativa</MenuItem>
              <MenuItem value="PAUSED">Pausada</MenuItem>
              <MenuItem value="FINISHED">Encerrada</MenuItem>
              <MenuItem value="EXPIRED">Expirada</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal}>Cancelar</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CouponCampaigns;
